import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as api from '../api';
import { RNLLoader } from '../components/UI';
import MRDashboard from '../components/materialRequest/MRDashboard';
import MRNewRequest from '../components/materialRequest/MRNewRequest';
import MRMyRequests from '../components/materialRequest/MRMyRequests';
import MRCCQueue from '../components/materialRequest/MRCCQueue';
import MRStoreQueue from '../components/materialRequest/MRStoreQueue';
import MRMgmtQueue from '../components/materialRequest/MRMgmtQueue';
import MRApprovedList from '../components/materialRequest/MRApprovedList';
import MRLoadTakeoffs from '../components/materialRequest/MRLoadTakeoffs';

export default function MaterialRequestPage() {
  const navigate = useNavigate();
  const params = useParams();
  const tabFromPath = params.tab;
  const [searchParams] = useSearchParams();
  const activeTab = tabFromPath || searchParams.get('tab') || 'dashboard';

  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    api.getMe().then(setCurrentUser).catch(() => {});
  }, []);

  const roles = (currentUser?.roles || []).map(r =>
    typeof r === 'string' ? r.toLowerCase() : (r.role_name || r.name || '').toLowerCase()
  );

  const isSuperAdmin = roles.includes('super_admin');
  const canSeeCCQueue = isSuperAdmin || roles.includes('cost_control');
  const canSeeStoreQueue = isSuperAdmin || roles.includes('store_keeper') || roles.includes('warehouse');
  const canSeeMgmtQueue = isSuperAdmin || roles.includes('md') || roles.includes('dmd') || roles.includes('project_manager');
  const rolesKnown = currentUser !== null;

  const goToTab = useCallback((id) => {
    api.clearApiCache();
    navigate(`/material-request/${id}`);
  }, [navigate]);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'new-request', label: 'New Request' },
    { id: 'my-requests', label: 'My Requests' },
  ];
  if (canSeeCCQueue) navItems.push({ id: 'cc-review-queue', label: 'CC Review Queue' });
  if (canSeeStoreQueue) navItems.push({ id: 'store-review-queue', label: 'Store Review Queue' });
  if (canSeeMgmtQueue) navItems.push({ id: 'mgmt-approval-queue', label: 'Approval Queue' });
  navItems.push({ id: 'approved', label: 'Approved Requests' });

  // Reached from the dashboard (button and card), not from a tab pill.
  const PAGE_ONLY = ['load-takeoffs'];
  const OPEN_TABS = ['dashboard', 'new-request', 'my-requests', 'approved', ...PAGE_ONLY];
  const awaitingRoles = !rolesKnown && !OPEN_TABS.includes(activeTab);
  const shownTab = (!rolesKnown || navItems.some(i => i.id === activeTab) || PAGE_ONLY.includes(activeTab))
    ? activeTab
    : 'dashboard';
  // ?takeoff=<id> on New Request: the form is filled in from that approved take-off.
  const takeoffId = shownTab === 'new-request' ? searchParams.get('takeoff') : null;

  return (
    <div className="fade-in" style={{ padding: '0 0 24px', marginTop: -28 }}>
      {/* Tab pills */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${navItems.length}, 1fr)`, gap: 10, marginBottom: 20, paddingTop: 16 }}>
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => goToTab(item.id)}
            style={{
              padding: '18px 10px',
              background: shownTab === item.id ? '#0b318bff' : 'var(--bg-card)',
              color: shownTab === item.id ? '#fff' : 'var(--text-primary)',
              border: '1px solid black',
              borderRadius: 12,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              boxShadow: shownTab === item.id ? '0 4px 12px rgba(11,49,139,0.2)' : 'var(--shadow-sm)',
              transition: 'all 0.2s ease',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ background: 'var(--bg-card)', border: '3px solid black', borderRadius: 16, minHeight: 400, padding: 24, boxShadow: 'var(--shadow-sm)' }}>
        {awaitingRoles && <RNLLoader title="Loading..." />}

        {shownTab === 'dashboard' && (
          <MRDashboard
            onNewRequest={() => goToTab('new-request')}
            onLoadTakeoffs={() => goToTab('load-takeoffs')}
            onGoToCC={() => goToTab('cc-review-queue')}
            onGoToStore={() => goToTab('store-review-queue')}
            onGoToMgmt={() => goToTab('mgmt-approval-queue')}
            onGoToApproved={() => goToTab('approved')}
          />
        )}
        {shownTab === 'new-request' && (
          <MRNewRequest key={takeoffId || 'blank'} takeoffId={takeoffId} onSaved={() => goToTab('my-requests')} />
        )}
        {shownTab === 'load-takeoffs' && (
          <MRLoadTakeoffs
            onBack={() => goToTab('dashboard')}
            onLoad={(id) => { api.clearApiCache(); navigate(`/material-request/new-request?takeoff=${id}`); }}
          />
        )}
        {shownTab === 'my-requests' && (
          <MRMyRequests onOpen={(id) => navigate(`/material-request/my-requests/${id}`)} />
        )}
        {shownTab === 'cc-review-queue' && canSeeCCQueue && (
          <MRCCQueue onOpen={(id) => navigate(`/material-request/cc-review-queue/${id}`)} />
        )}
        {shownTab === 'store-review-queue' && canSeeStoreQueue && (
          <MRStoreQueue onOpen={(id) => navigate(`/material-request/store-review-queue/${id}`)} />
        )}
        {shownTab === 'mgmt-approval-queue' && canSeeMgmtQueue && (
          <MRMgmtQueue onOpen={(id) => navigate(`/material-request/mgmt-approval-queue/${id}`)} />
        )}
        {shownTab === 'approved' && (
          <MRApprovedList onOpen={(id) => navigate(`/material-request/approved/${id}`)} />
        )}
      </div>

      {/* Lifecycle Stages — outside the main card, dashboard tab only */}
      {shownTab === 'dashboard' && (
        <div style={{ marginTop: 16, background: 'var(--bg-card)', border: '3px solid black', borderRadius: 12, padding: '12px 20px', overflowX: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 'max-content' }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', marginRight: 6 }}>
              MR PIPELINE:
            </span>
            {[
              { label: 'Take-Off',              bg: '#f3f4f6', color: '#374151' },
              { label: 'Take-Off Approval',     bg: '#fef9c3', color: '#854d0e' },
              { label: 'Material Request',      bg: '#dbeafe', color: '#1e40af' },
              { label: 'Cost Control Review',   bg: '#fde68a', color: '#92400e' },
              { label: 'Store Review',          bg: '#ede9fe', color: '#5b21b6' },
              { label: 'Management Approval',   bg: '#e0e7ff', color: '#3730a3' },
              { label: 'Procurement Request',   bg: '#ccfbf1', color: '#065f46' },
              { label: 'Quotation Comparison',  bg: '#cffafe', color: '#155e75' },
              { label: 'Supplier Selection',    bg: '#bae6fd', color: '#0c4a6e' },
              { label: 'Purchase Order',        bg: '#bbf7d0', color: '#14532d' },
              { label: 'GRN',                   bg: '#a7f3d0', color: '#065f46' },
              { label: 'Store Receipt',         bg: '#d1fae5', color: '#064e3b' },
              { label: 'Site Issue',            bg: '#86efac', color: '#14532d' },
            ].map(({ label, bg, color }, i, arr) => (
              <React.Fragment key={label}>
                <span style={{
                  fontSize: 12, fontWeight: 600, background: bg, color,
                  borderRadius: 8, padding: '6px 14px', whiteSpace: 'nowrap',
                  border: `1px solid ${color}33`,
                }}>
                  {label}
                </span>
                {i < arr.length - 1 && (
                  <span style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }}>→</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
